// components/CountrySummary.js - FIXED: Manual control for origin filter
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { styles } from '../styles';
import CountryTable from './CountryTable';
import CountryModal from './CountryModal';
import { analyzeCountries } from '../services/api/analysisApi';

const CountrySummary = ({ searchTerm, originFilter, selectedTable, countryFilter }) => {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [countryData, setCountryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [analysisInfo, setAnalysisInfo] = useState(null);

    // 🚨 NEW: Track applied filters separately from props
    const [appliedFilters, setAppliedFilters] = useState({
        searchTerm: '',
        originFilter: 'All Origins',
        countryFilter: 'All Countries',
        selectedTable: ''
    });

    // Handle country click to open modal
    const handleCountryClick = (country) => {
        console.log('Opening modal for country:', country.country);
        setSelectedCountry(country);
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        console.log('Closing modal');
        setShowModal(false);
        setSelectedCountry(null);
    };

    // Fetch country analysis from Python script with ALL filters
    const fetchCountryAnalysis = useCallback(async (filters = null) => {
        // Use provided filters or current applied filters
        const filtersToUse = filters || appliedFilters;

        setLoading(true);
        setError('');

        try {
            if (filtersToUse.searchTerm && filtersToUse.searchTerm.trim()) {
                console.log('🔍 Python Analysis: Search term ->', filtersToUse.searchTerm.trim());
            }

            if (filtersToUse.originFilter && filtersToUse.originFilter !== 'All Origins') {
                console.log('🔍 Python Analysis: Origin filter ->', filtersToUse.originFilter);
            }

            if (filtersToUse.countryFilter && filtersToUse.countryFilter !== 'All Countries') {
                console.log('🔍 Python Analysis: Country filter ->', filtersToUse.countryFilter);
            }

            console.log('🐍 Starting Python analysis with filters:', {
                searchTerm: filtersToUse.searchTerm || 'none',
                originFilter: filtersToUse.originFilter || 'none',
                countryFilter: filtersToUse.countryFilter || 'none',
                table: filtersToUse.selectedTable
            });

            const result = await analyzeCountries({
                selectedTable: filtersToUse.selectedTable,
                searchTerm: filtersToUse.searchTerm,
                originFilter: filtersToUse.originFilter,
                countryFilter: filtersToUse.countryFilter
            });

            if (result.success) {
                setCountryData(result.countries);
                setAnalysisInfo(result);
                setError('');

                // Update applied filters to match what was actually used
                setAppliedFilters(filtersToUse);

                console.log('✅ Python analysis completed:', {
                    totalRecords: result.total_records,
                    uniqueDestinations: result.unique_destinations,
                    countriesFound: result.countries.length
                });
            } else {
                setError(result.error || 'Analysis failed');
                setCountryData([]);
                console.error('❌ Python analysis failed:', result.error);
            }
        } catch (err) {
            setError('Failed to fetch country analysis: ' + err.message);
            setCountryData([]);
            console.error('❌ Python analysis error:', err);
        } finally {
            setLoading(false);
        }
    }, [appliedFilters]);

    // 🚨 NEW: Manual refresh function for manual control
    const handleManualRefresh = () => {
        const currentFilters = {
            searchTerm,
            originFilter,
            countryFilter,
            selectedTable
        };

        console.log('🔄 CountrySummary: Manual refresh triggered with current filters:', currentFilters);
        fetchCountryAnalysis(currentFilters);
    };

    // Track the previous table so we can detect when the component re-mounts on a new table
    const prevTableRef = useRef(null);

    // Auto-run on mount and when table / search / country filter changes (NOT origin — that requires manual refresh)
    useEffect(() => {
        if (!selectedTable) return;

        const tableChanged   = selectedTable !== prevTableRef.current;
        const searchChanged  = searchTerm    !== appliedFilters.searchTerm;
        const countryChanged = countryFilter !== appliedFilters.countryFilter;
        const isFirstLoad    = appliedFilters.selectedTable === '';

        const shouldAutoRun = isFirstLoad || tableChanged || searchChanged || countryChanged;

        if (shouldAutoRun) {
            prevTableRef.current = selectedTable;
            const autoFilters = {
                searchTerm,
                originFilter: appliedFilters.originFilter, // keep previously applied origin
                countryFilter,
                selectedTable
            };
            fetchCountryAnalysis(autoFilters);
        }
    }, [searchTerm, countryFilter, selectedTable, appliedFilters, fetchCountryAnalysis]); // originFilter intentionally excluded — manual refresh only

    // Check if current filters differ from applied filters
    const hasUnappliedChanges =
        searchTerm !== appliedFilters.searchTerm ||
        originFilter !== appliedFilters.originFilter ||
        countryFilter !== appliedFilters.countryFilter ||
        selectedTable !== appliedFilters.selectedTable;

    // Specifically check if only origin filter changed
    const onlyOriginFilterChanged =
        searchTerm === appliedFilters.searchTerm &&
        originFilter !== appliedFilters.originFilter &&
        countryFilter === appliedFilters.countryFilter &&
        selectedTable === appliedFilters.selectedTable;

    // Loading state
    if (loading) {
        return (
            <div style={styles.tableContainer}>
                <div style={styles.tableHeader}>
                    <h2 style={styles.tableTitle}>Country Summary</h2>
                    <div style={styles.loadingContainer}>
                        <RefreshCw size={32} style={{ color: '#a855f7', animation: 'spin 1s linear infinite', marginRight: '12px' }} />
                        <span>Analyzing data with Python script...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={styles.tableContainer}>
                <div style={styles.tableHeader}>
                    <h2 style={styles.tableTitle}>Country Summary</h2>
                    <div style={styles.errorAlert}>
                        <span>Error: {error}</span>
                        <button onClick={handleManualRefresh} style={{ ...styles.button, ...styles.buttonSecondary, marginLeft: '10px' }}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Main render
    return (
        <>
            {/* 🚨 NEW: Manual refresh notice when filters changed */}
            {hasUnappliedChanges && (
                <div style={{
                    margin: '0 0 16px 0',
                    padding: '12px 16px',
                    backgroundColor: onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                    border: `1px solid ${onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <div style={{
                            color: onlyOriginFilterChanged ? '#f59e0b' : '#3b82f6',
                            fontWeight: '600',
                            marginBottom: '4px'
                        }}>
                            {onlyOriginFilterChanged ? 'Origin Filter Changed' : 'Filters Changed'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            {onlyOriginFilterChanged
                                ? `Click "Apply Origin Filter" to analyze with origin: "${originFilter}"`
                                : 'Some filters have changed. Click "Refresh Analysis" to update results.'
                            }
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                            Current: {originFilter} | Applied: {appliedFilters.originFilter}
                        </div>
                    </div>
                    <button
                        onClick={handleManualRefresh}
                        style={{
                            ...styles.button,
                            backgroundColor: onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                            border: `1px solid ${onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.4)' : 'rgba(59, 130, 246, 0.4)'}`,
                            color: onlyOriginFilterChanged ? '#fbbf24' : '#60a5fa',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = onlyOriginFilterChanged ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)';
                        }}
                    >
                        <Search size={14} />
                        {onlyOriginFilterChanged ? 'Apply Origin Filter' : 'Refresh Analysis'}
                    </button>
                </div>
            )}

            <CountryTable
                countryData={countryData}
                analysisInfo={analysisInfo}
                countryFilter={countryFilter}
                onCountryClick={handleCountryClick}
            />

            <CountryModal
                showModal={showModal}
                selectedCountry={selectedCountry}
                onClose={closeModal}
            />
        </>
    );
};

export default CountrySummary;
